'use strict';

const libPictView = require('pict-view');
const libPictSectionMapping = require('pict-section-mapping');

const html = String.raw;

// The embedded section view's identity + the mount point this page's template provides for it.
const MAPPER_VIEW_HASH = 'PSM-DataMapper';
const MOUNT_SELECTOR = '#DataMapper-Mount';

/**
 * Host page for the visual data-mapper (pict-section-mapping).
 *
 * The section is a self-contained Pict view that drives its own REST client against APIBaseUrl
 * ('/1.0/mapper' — the PDO mapper surface, reached same-origin via Kong so the session cookie carries
 * auth). Like DataExplorer/ConfigViewer, the section is registered + rendered lazily in onAfterRender —
 * the mount div only exists once this page's template is in the DOM, and the main render replaces the
 * container each visit, so we (re)mount the section every paint. Modal confirm/toast resolve to the
 * app's existing 'Pict-Section-Modal' (the section's _modal() falls back to it).
 */
class DataMapper extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	onAfterRender(pRenderable)
	{
		this.pict.CSSMap.injectCSS();
		this._mountMapper();
		return super.onAfterRender(pRenderable);
	}

	/** (Re)mount the embedded mapping section into the page's mount div. */
	_mountMapper()
	{
		if (this.pict.ContentAssignment.getElement(MOUNT_SELECTOR).length === 0) { return; }
		const tmpView = this._ensureMapperView();
		if (!tmpView) { return; }
		tmpView.options.DefaultDestinationAddress = MOUNT_SELECTOR;
		tmpView.render();
	}

	/** Lazily register the pict-section-mapping section view pointed at the PDO /1.0/mapper surface. */
	_ensureMapperView()
	{
		if (this.pict.views[MAPPER_VIEW_HASH]) { return this.pict.views[MAPPER_VIEW_HASH]; }
		this.pict.addView(MAPPER_VIEW_HASH, Object.assign({}, libPictSectionMapping.default_configuration,
			{
				ViewIdentifier: MAPPER_VIEW_HASH,
				APIBaseUrl: '/1.0/mapper',
				Mode: 'manage',
				ShowToolbar: true,
				AutoRender: false,
				DefaultDestinationAddress: MOUNT_SELECTOR,
			}), libPictSectionMapping);
		const tmpView = this.pict.views[MAPPER_VIEW_HASH];
		// A view added after the boot cycle isn't initialized automatically — initialize before first render.
		if (tmpView && typeof tmpView.initialize === 'function') { tmpView.initialize(); }
		return tmpView;
	}
}

module.exports = DataMapper;

module.exports.default_configuration =
{
	ViewIdentifier: 'DataMapper',
	DefaultRenderable: 'DataMapperWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
.datamapper-head { display: flex; align-items: center; gap: 0.9rem; max-width: 1040px; margin: 0 auto 1.1rem; }
.datamapper-ic { display: inline-flex; align-items: center; justify-content: center; width: 2.6rem; height: 2.6rem; border-radius: 12px;
	background: var(--theme-color-background-tertiary, #eef1f5); color: var(--theme-color-brand-primary, #156dd1); font-size: 1.2rem; flex: 0 0 auto; }
.datamapper-title { font-size: 1.5rem; font-weight: 700; line-height: 1.1; color: var(--theme-color-text-primary, #1f2733); margin: 0; }
.datamapper-sub { font-size: 0.92rem; color: var(--theme-color-text-muted, #6b7686); margin: 0.15rem 0 0; }
.datamapper-panel { max-width: 1040px; margin: 0 auto; background: var(--theme-color-background-primary, #fff);
	border: 1px solid var(--theme-color-border-light, #eef1f5); border-radius: 12px; padding: 0.9rem 1rem; min-height: 60vh; }
`,

	Templates:
	[
		{
			Hash: 'DataMapper-Template',
			Template: html`
				<section class="section">
					<div class="container">
						<div class="datamapper-head">
							<span class="datamapper-ic"><i class="fas fa-diagram-project"></i></span>
							<div>
								<h1 class="datamapper-title">Data Mapper</h1>
								<p class="datamapper-sub">Define source→target field mappings — the operations that compile materialized projections for your data lake.</p>
							</div>
						</div>
						<div class="datamapper-panel">
							<div id="DataMapper-Mount"></div>
						</div>
					</div>
				</section>`,
		},
	],

	Renderables:
	[
		{
			RenderableHash: 'DataMapperWrap',
			TemplateHash: 'DataMapper-Template',
			DestinationAddress: '#PlatformConfiguration-Application-Container',
		},
	],
};
